import { z } from 'zod';
import { SchemaDate, SchemaMonthYear, SchemaLocation } from '@axiom/types';

const BaseCandidateExperienceFormFields = z.object({
  isAxiom: z.boolean().optional(),
  isConfidential: z.boolean().nullable().optional(),
  currentlyWorkHere: z.boolean().optional(),
  description: z.string().nullable().optional(),
  client: z.string().max(255),
  externalOpportunityName: z.string().max(255).nullable().optional(),
  industryValue: z.string(),
  industryIsOverridden: z.boolean().optional(),
  id: z.string().optional(),
  opportunity: z
    .object({
      jobName: z.string().optional(),
      locationCityStateCountry: z.string().optional(),
      position: z
        .object({
          worksite: z.string().optional(),
        })
        .nullable()
        .optional(),
    })
    .nullish(),
  location: SchemaLocation.nullish(),
});

const dateValidationRefinement = (
  data: {
    startDate?: string;
    endDate?: string | null;
  },
  ctx: z.RefinementCtx
) => {
  if (data.startDate) {
    const start = new Date(data.startDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (start > today) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Start date cannot be in the future',
        path: ['startDate'],
      });
    }
  }

  if (data.endDate && data.startDate) {
    const start = new Date(data.startDate);
    const end = new Date(data.endDate);
    if (end <= start) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'End date must be later than start date',
        path: ['endDate'],
      });
    }
  }
};

export const CandidateExperienceFormAxiomSchema =
  BaseCandidateExperienceFormFields.extend({
    startDate: SchemaDate,
    endDate: SchemaDate,
  }).superRefine(dateValidationRefinement);

export const CandidateExperienceFormNonAxiomSchema =
  BaseCandidateExperienceFormFields.extend({
    startDate: SchemaMonthYear,
    endDate: SchemaMonthYear.nullish(),
  }).superRefine((data, ctx) => {
    // Run date validation refinement
    dateValidationRefinement(data, ctx);

    // If not currently working here, endDate is required
    if (!data.currentlyWorkHere && !data.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'End date is required',
        path: ['endDate'],
      });
    }
  });

export const CandidateExperienceFormSchema = CandidateExperienceFormAxiomSchema;

export type CandidateExperienceFormData =
  | z.infer<typeof CandidateExperienceFormAxiomSchema>
  | z.infer<typeof CandidateExperienceFormNonAxiomSchema>;
